import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type {
	MessageAction,
	MessageActionInvocationStatus,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/common/types.tsx';
import type {
	OnClickErrorProps,
	OnClickSuccessProps,
} from '@atlassian/jira-create-work-items/createButton.tsx';
import { useDraftWorkItemsActions } from '@atlassian/jira-create-work-items/draft-work-items-store.tsx';
import {
	useAreAllCreated,
	useAreAllDismissed,
	useHasAppliedDraftEdits,
	useGetActiveOrFinishedDraftWorkItemsCount,
	useGetActivePlannedChildInvocationIdsByParent,
} from '@atlassian/jira-create-work-items/draft-work-items-store/hooks.tsx';
import {
	getOrFetchDraftWorkItemsWithEdits,
	resolveDraftWorkItemInMonolith,
	mapStoreItemToDraftWorkItemWithEdits,
	type GetDraftWorkItemsResponse,
} from '@atlassian/jira-create-work-items/services/draft-work-items.ts';
import { useAiCreateProjectContext } from '@atlassian/jira-create-work-items/useAiCreateProjectContext.tsx';
import { useAifcAnalytics } from '@atlassian/jira-create-work-items/useAifcAnalytics.tsx';
import { useCreateDraftWorkItems } from '@atlassian/jira-create-work-items/useCreateDraftWorkItems.tsx';
import { recalculateHierarchy } from '@atlassian/jira-create-work-items/utils/recalculateHierarchy.tsx';
import { useWorkItemPreviewPanel } from '@atlassian/preview-panel-work-item-view';
import type { JiraCreateItemsAction } from '@atlassian/rovo-content-bridge-api-commands/commands/jira-create-items-command';

import { MESSAGE_ACTION_RESOLUTIONS } from '../../../common/constants';
import type { ResolveActionsFnc } from '../../../common/types';
import { ErrorMessage } from '../../../common/ui/error-message';
import type { GicModalProps } from '../../../common/ui/gic-modal';
import { SystemMessage } from '../../../common/ui/system-message';
import {
	createWorkItemAri,
	isSubTask,
} from '../../../common/utils/jira-create-with-rovo-preview-card';

import type { jiraCreateWithRovoPreviewCardQuery } from './__generated__/jiraCreateWithRovoPreviewCardQuery.graphql';
import { FallbackPreviewCard } from './fallback-card';
import { PreviewFooterActions } from './footer-actions';
import { JiraMultiWorkItemPreviewCard } from './jira-multi-work-item-card';
import { JiraSingleWorkItemPreviewCard } from './jira-single-work-item-card';
import { ModalSyncBridgeAPI } from './modal-sync-bridge-api';

const messages = defineMessages({
	createdSuggestedWorkItem: {
		id: 'jiraCreateWithRovoPreviewCard.createdSuggestedWorkItem',
		defaultMessage: "You've created {issueKey} {summary}",
		description: 'System message displayed when all suggested work items are created',
	},
	createdMultiWorkItem: {
		id: 'jiraCreateWithRovoPreviewCard.createdMultiWorkItem',
		defaultMessage: "You've created {count} work items",
		description: 'System message displayed when all suggested work items are created',
	},
	dismissedSuggestedWorkItem: {
		id: 'jiraCreateWithRovoPreviewCard.dismissedSuggestedWorkItem',
		defaultMessage: "You've dismissed all suggested work items",
		description: 'System message displayed when all suggested work items are dismissed',
	},
	singleWorkItemPreviewHeaderTitle: {
		id: 'jiraCreateWithRovoPreviewCard.singleWorkItemPreviewHeaderTitle',
		defaultMessage: 'Create work item',
		description: 'The title of the single work item preview card for the Jira AIFC experience',
	},
	multiWorkItemPreviewHeaderTitle: {
		id: 'jiraCreateWithRovoPreviewCard.multiWorkItemPreviewHeaderTitle',
		defaultMessage: 'Create work items',
		description: 'The title of the multi work item preview card for the Jira AIFC experience',
	},
	projectChangePrompt: {
		id: 'jiraCreateWithRovoPreviewCard.projectChangePrompt',
		defaultMessage: 'Change the project to {projectKey}',
		description: 'The prompt displayed when the user changes the project in the preview card',
	},
});

// The maximum time to display the fallback preview card when waiting for the BE inline edits to be fetched.
// If the fetch is resolved before this time, the list will be rendered normally.
// Once this time is elapsed, the list will be rendered normally regardless.
// If the fetch resolves later, overrides still apply without the loading state.
// Time of 1000ms is arbitrarily chosen - can be adjusted.
const FETCH_EDITS_LOADING_TIME = 1000;
const JIRA_PREVIEW_CARD_ENTRY_POINT = 'rovoChatPreviewCard';

/**
 * Renders the single work item when there is a single work item suggestion
 * and the mini list view when there are multiple work item suggestions
 */
const JiraCreateWithRovoPreviewCardNew = ({
	product,
	actions: initialActions,
	messageId,
	cloudId,
	conversationId,
	isSelected,
	isEnabled,
	onPreviewClick,
	resolveActions,
	isStagingArea,
	showSuccessFlagOnCreate,
}: {
	product?: string;
	actions: MessageAction[];
	messageId: string;
	cloudId: string;
	conversationId: string;
	isSelected: boolean;
	isEnabled?: boolean;
	onPreviewClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	resolveActions: ResolveActionsFnc;
	isStagingArea: boolean;
	showSuccessFlagOnCreate?: boolean;
}) => {
	const hasFiredViewEvent = useRef(false);
	const { sendAifcAnalyticsEvent } = useAifcAnalytics(messageId);
	const publish = usePublish('ai-mate');
	const experienceTracker = useAIMateExperienceTracker();
	const actionKey = initialActions[0]?.key;
	const projectIdOrKey = (initialActions as JiraCreateItemsAction[])[0]?.data?.projectIdOrKey ?? '';

	const queryRef = useLazyLoadQuery<jiraCreateWithRovoPreviewCardQuery>(
		graphql`
			query jiraCreateWithRovoPreviewCardQuery($cloudId: ID!, $projectIdOrKey: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					cloudUrl
				}

				...useAiCreateProjectContextQuery
					@arguments(cloudId: $cloudId, projectIdOrKey: $projectIdOrKey)
				...jiraSingleWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraSingleWorkItemPreviewCard
				...footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_query

				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
					...jiraMultiWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraMultiWorkItemPreviewCard
					...footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_project
				}
			}
		`,
		{
			cloudId,
			projectIdOrKey,
		},
	);

	useEffect(() => {
		if (!hasFiredViewEvent.current) {
			hasFiredViewEvent.current = true;

			sendAifcAnalyticsEvent({
				action: 'rendered',
				actionSubject: 'component',
				actionSubjectId: 'createWorkItemsPreviewCard',
				attributes: {
					itemCount: initialActions.length,
					isSingleWorkItem: initialActions.length === 1,
					isStagingArea,
				},
			});
		}
		// Empty dependency array to ensure that we only fire the analytics event once when the component renders
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const {
		draftWorkItems,
		isStoreReady,
		onDismissAllDraftWorkItems,
		onFieldChange,
		onWorkItemsCreated,
		removeWorkItem,
	} = useAiCreateProjectContext({
		project: queryRef,
		actions: initialActions as JiraCreateItemsAction[],
		isActiveMessage: isEnabled,
		messageId,
		conversationId,
		cloudId,
		sendAifcAnalyticsEvent,
	});

	const isCreated = useAreAllCreated(messageId);
	const isDismissed = useAreAllDismissed(messageId);
	const activeChildrenInvocationIdsNew = useGetActivePlannedChildInvocationIdsByParent(messageId);

	const activeOrFinishedDraftWorkItemsCount = useGetActiveOrFinishedDraftWorkItemsCount(messageId);
	const { getDraftWorkItemsWithoutHierarchy, getAllActiveInvocationIds, setDraftWorkItems } =
		useDraftWorkItemsActions();

	// On mount, we fetch the BE state of the draft work items and apply it to the sweet state.
	// We use sweet state (hasAppliedDraftEdits) instead of a ref so the flag persists across remounts.
	const hasAppliedDraftEdits = useHasAppliedDraftEdits(messageId);

	// Show a loading state while waiting for BE draft overrides, for a maximum of 1000ms.
	// If we've already applied draft edits (hasAppliedDraftEdits = true), skip the loading state entirely.
	const [isFetchingDraftWorkItems, setisFetchingDraftWorkItems] = useState(!hasAppliedDraftEdits);
	useEffect(() => {
		if (
			// Ensure the store is ready before applying the draft overrides.
			!isStoreReady ||
			hasAppliedDraftEdits ||
			!conversationId ||
			!messageId
		) {
			return;
		}

		const timeout = setTimeout(() => setisFetchingDraftWorkItems(false), FETCH_EDITS_LOADING_TIME);

		getOrFetchDraftWorkItemsWithEdits(
			cloudId,
			conversationId,
			messageId,
			sendAifcAnalyticsEvent,
		).then((draftActions: GetDraftWorkItemsResponse | null) => {
			clearTimeout(timeout);
			setisFetchingDraftWorkItems(false);

			if (!draftActions?.length) {
				return;
			}

			const currentItems = getDraftWorkItemsWithoutHierarchy(messageId);
			if (!currentItems.length) {
				return;
			}

			const updatedItems: Record<string, DraftWorkItem> = Object.fromEntries(
				currentItems.map((item) => [item.invocationId, item]),
			);

			// Replace items via their invocationId.
			for (const { invocationId, data } of draftActions) {
				if (updatedItems[invocationId]) {
					// Filter out any work items that were previously cancelled and persisted to the monolith.
					if (
						data.resolution === MESSAGE_ACTION_RESOLUTIONS.cancel &&
						FeatureGates.getExperimentValue(
							'jira-aifc-workflow-resumption-ui-updates',
							'isEnabled',
							false,
							{ fireExperimentExposure: false },
						)
					) {
						// Cancel all child work items of the parent work item
						updatedItems[invocationId]?.childInvocationIds?.forEach((childInvocationId) => {
							delete updatedItems[childInvocationId];
						});
						delete updatedItems[invocationId];
						continue;
					}

					const oldSummary = updatedItems[invocationId].summary;
					const newSummary = data.summary ?? oldSummary;

					// Update a work item's parent summary to ensure the parent <=> child mapping stays stable to be used within recalculateHierarchy.
					if (oldSummary !== newSummary) {
						Object.values(updatedItems).forEach((item) => {
							if (item.fields.parent === oldSummary) {
								item.fields = { ...item.fields, parent: newSummary };
							}
						});
					}

					// The createdIssueKey is only saved in the actions array, so ensure we preserve this value if it exists.
					const createdIssueKey = updatedItems[invocationId]?.createdIssueKey;

					updatedItems[invocationId] = {
						invocationId,
						...(createdIssueKey && {
							createdIssueKey,
						}),
						...data,
					};
				}
			}

			// If all work items are cancelled, there should be no items in the store.
			if (
				!Object.keys(updatedItems).length &&
				FeatureGates.getExperimentValue(
					'jira-aifc-workflow-resumption-ui-updates',
					'isEnabled',
					false,
					{ fireExperimentExposure: false },
				)
			) {
				setDraftWorkItems(messageId, {}, []);
				return;
			}

			// Resolve hierarchy before setting store state, since parent summaries may have
			// changed and children's fields.parent needs to be re-resolved.
			const { newMap, childInvocationIdsSet } = recalculateHierarchy({
				itemsMap: updatedItems,
			});
			const topLevelInvocationIds = Object.values(newMap)
				.filter(
					(item) => item.status !== 'cancelled' && !childInvocationIdsSet.has(item.invocationId),
				)
				.map((item) => item.invocationId);
			setDraftWorkItems(messageId, newMap, topLevelInvocationIds);
		});
	}, [
		draftWorkItems,
		conversationId,
		messageId,
		getDraftWorkItemsWithoutHierarchy,
		getAllActiveInvocationIds,
		setDraftWorkItems,
		cloudId,
		sendAifcAnalyticsEvent,
		isStoreReady,
		hasAppliedDraftEdits,
	]);

	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [isProjectChangePending, setIsProjectChangePending] = useState(false);
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
	const [isCreating, setIsCreating] = useState(false);

	const onToggleSelect = useCallback(
		(invocationId: string, newValue: boolean) => {
			setSelectedItems((prev) => {
				const next = new Set(prev);
				if (newValue) {
					next.add(invocationId);
				} else {
					next.delete(invocationId);

					// If the parent has child subtasks, uncheck them.
					const parentItem = draftWorkItems.find((item) => item.invocationId === invocationId);
					parentItem?.childWorkItems?.forEach((child) => {
						if (isSubTask(child)) {
							next.delete(child.invocationId);
						}
					});
				}
				return next;
			});
		},
		[draftWorkItems],
	);
	const hasAnyCreatedItems = useMemo(
		() =>
			FeatureGates.getExperimentValue<string>(
				'jira-aifc-workflow-resumption-ui-updates',
				'cohort',
				'control',
				{ fireExperimentExposure: false },
			) === 'checkboxes' &&
			draftWorkItems.some(
				(item) =>
					Boolean(item.createdIssueKey) ||
					item.childWorkItems?.some((child) => Boolean(child.createdIssueKey)),
			),
		[draftWorkItems],
	);

	const { formatMessage } = useIntl();
	const { openPreviewPanel } = useWorkItemPreviewPanel();
	const cloudUrl = queryRef.tenantContexts?.[0]?.cloudUrl ?? '';
	const primaryDraftWorkItem = getDraftWorkItemsWithoutHierarchy(messageId)?.[0];

	// Handles any side effects that need to be performed after work item creation
	const onCreateSuccess = useCallback(
		({ invocationIdToCreatedIssueKey }: OnClickSuccessProps) => {
			setIsCreating(false);
			const createdIssueKeys = Object.values(invocationIdToCreatedIssueKey);
			if (createdIssueKeys.length > 0) {
				resolveActions?.(
					actionKey,
					MESSAGE_ACTION_RESOLUTIONS.actioned,

					Object.keys(invocationIdToCreatedIssueKey),
					invocationIdToCreatedIssueKey,
				);

				onWorkItemsCreated({ invocationIdToCreatedIssueKey });
				setErrorMessage(undefined);
			}
		},
		[actionKey, onWorkItemsCreated, resolveActions],
	);

	const onCreateError = useCallback(
		({ errorMessage }: OnClickErrorProps) => {
			setIsCreating(false);
			setErrorMessage(errorMessage);
			sendAifcAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createWorkItemsError',
				attributes: {
					errorMessage,
					isSingleWorkItem: initialActions.length === 1,
					isStagingArea,
				},
			});
		},
		[sendAifcAnalyticsEvent, initialActions.length, isStagingArea],
	);

	const createDraftWorkItems = useCreateDraftWorkItems({
		onSuccess: onCreateSuccess,
		onError: onCreateError,
		cloudId,
		cloudUrl,
		projectIdOrKey,
		showErrorsInFlag: false,
		showSuccessFlagOnCreate,
		isStagingArea,
		entryPoint: JIRA_PREVIEW_CARD_ENTRY_POINT,
		sendAifcAnalyticsEvent,
	});

	const onBulkAccept = useCallback(() => {
		if (
			FeatureGates.getExperimentValue<string>(
				'jira-aifc-workflow-resumption-ui-updates',
				'cohort',
				'control',
				{ fireExperimentExposure: false },
			) === 'checkboxes'
		) {
			setIsCreating(true);
		}
		const draftWorkItemsToCreate = getDraftWorkItemsWithoutHierarchy(messageId);

		// If no items are selected for the checkbox variant, we will create all visible work items. Otherwise,
		// the count will be the number of selected items.
		const checkboxCount =
			selectedItems.size === 0 ? draftWorkItemsToCreate.length : selectedItems.size;
		sendAifcAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId:
				initialActions.length === 1 ? 'createWorkItemsSingleCreate' : 'createWorkItemsBulkCreate',
			attributes: {
				itemCount:
					FeatureGates.getExperimentValue<string>(
						'jira-aifc-workflow-resumption-ui-updates',
						'cohort',
						'control',
						{ fireExperimentExposure: false },
					) === 'checkboxes'
						? checkboxCount
						: draftWorkItemsToCreate.length,
				isSingleWorkItem: initialActions.length === 1,
				isStagingArea,
				totalCount: initialActions.length,
			},
		});
	}, [
		getDraftWorkItemsWithoutHierarchy,
		messageId,
		sendAifcAnalyticsEvent,
		initialActions.length,
		isStagingArea,
		selectedItems,
	]);

	const onInlineAccept = useCallback(
		({ draftWorkItem }: { draftWorkItem: DraftWorkItem }) => {
			sendAifcAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createWorkItemsSingleCreate',
				attributes: {
					itemCount: 1,
					isSingleWorkItem: false,
					isStagingArea,
				},
			});
			return createDraftWorkItems({ draftWorkItems: [draftWorkItem] });
		},
		[createDraftWorkItems, sendAifcAnalyticsEvent, isStagingArea],
	);

	const onDismissAll = useCallback(() => {
		const draftItemsWithoutHierarchy = getDraftWorkItemsWithoutHierarchy(messageId);
		const activeInvocationIds = getAllActiveInvocationIds(messageId);

		if (
			FeatureGates.getExperimentValue(
				'jira-aifc-workflow-resumption-ui-updates',
				'isEnabled',
				false,
				{
					fireExperimentExposure: false,
				},
			)
		) {
			for (const invocationId of activeInvocationIds) {
				const storeItem = draftItemsWithoutHierarchy.find(
					(item) => item.invocationId === invocationId,
				);
				if (storeItem) {
					// For workflow resumption, we don't want to resolve decline within Convo-AI, so we will
					// post the status to the Jira monolith.
					const draftAction = mapStoreItemToDraftWorkItemWithEdits(storeItem);
					resolveDraftWorkItemInMonolith(
						cloudId,
						conversationId,
						messageId,
						draftAction,
						MESSAGE_ACTION_RESOLUTIONS.cancel,
						sendAifcAnalyticsEvent,
						experienceTracker,
					);
				}
			}
		} else {
			resolveActions?.(actionKey, MESSAGE_ACTION_RESOLUTIONS.cancel, activeInvocationIds);
		}

		onDismissAllDraftWorkItems();
		setErrorMessage(undefined);
		sendAifcAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId:
				initialActions.length === 1 ? 'createWorkItemsSingleDismiss' : 'createWorkItemsBulkDismiss',
			attributes: {
				itemCount: activeInvocationIds.length,
				isSingleWorkItem: initialActions.length === 1,
				isStagingArea,
			},
		});
	}, [
		actionKey,
		getAllActiveInvocationIds,
		messageId,
		onDismissAllDraftWorkItems,
		resolveActions,
		sendAifcAnalyticsEvent,
		initialActions.length,
		isStagingArea,
		conversationId,
		cloudId,
		experienceTracker,
		getDraftWorkItemsWithoutHierarchy,
	]);

	const onDeclineWorkItem = useCallback(
		({ invocationId }: { invocationId: string }) => {
			const draftItemsWithoutHierarchy = getDraftWorkItemsWithoutHierarchy(messageId);
			const workItemsToDecline = [
				invocationId,
				...(activeChildrenInvocationIdsNew[invocationId] ?? []),
			];

			if (
				FeatureGates.getExperimentValue(
					'jira-aifc-workflow-resumption-ui-updates',
					'isEnabled',
					false,
					{ fireExperimentExposure: false },
				)
			) {
				const storeItem = draftItemsWithoutHierarchy.find(
					(item) => item.invocationId === invocationId,
				);
				if (storeItem) {
					// For workflow resumption, we don't want to resolve decline within Convo-AI, so we will
					// post the status to the Jira monolith.
					const draftAction = mapStoreItemToDraftWorkItemWithEdits(storeItem);
					resolveDraftWorkItemInMonolith(
						cloudId,
						conversationId,
						messageId,
						draftAction,
						MESSAGE_ACTION_RESOLUTIONS.cancel,
						sendAifcAnalyticsEvent,
						experienceTracker,
					);
				}
			} else {
				resolveActions?.(actionKey, MESSAGE_ACTION_RESOLUTIONS.cancel, workItemsToDecline);
			}
			removeWorkItem(invocationId);
			sendAifcAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createWorkItemsSingleDismiss',
				attributes: {
					// If an epic is declined, we remove all child work items too
					itemCount: workItemsToDecline.length,
					isSingleWorkItem: initialActions.length === 1,
					isStagingArea,
				},
			});
		},
		[
			actionKey,
			initialActions.length,
			resolveActions,
			removeWorkItem,
			sendAifcAnalyticsEvent,
			isStagingArea,
			activeChildrenInvocationIdsNew,
			cloudId,
			conversationId,
			messageId,
			experienceTracker,
			getDraftWorkItemsWithoutHierarchy,
		],
	);

	const showSingleWorkItemCard = Boolean(initialActions.length === 1 && initialActions[0]);

	const gicEntryPointProps: GicModalProps = useMemo(
		() => ({
			projectId: undefined,
			actionKey,
			cloudId,
			onWorkItemsCreated,
			resolveActions,
			clearErrors: () => setErrorMessage(undefined),
			conversationId,
			messageId,
			showSuccessFlagOnCreate,
		}),
		[
			actionKey,
			cloudId,
			onWorkItemsCreated,
			resolveActions,
			setErrorMessage,
			conversationId,
			messageId,
			showSuccessFlagOnCreate,
		],
	);

	const FooterActions = useCallback(
		() => (
			<PreviewFooterActions
				product={product}
				query={queryRef}
				project={queryRef.jira_projectByIdOrKey!}
				areButtonsDisabled={isSelected || !isEnabled || isCreated || isProjectChangePending}
				cloudId={cloudId}
				messageId={messageId}
				projectIdOrKey={projectIdOrKey}
				isSingleWorkItem={showSingleWorkItemCard}
				onCreateSuccess={onCreateSuccess}
				onCreateError={onCreateError}
				onDismiss={onDismissAll}
				onPreviewClick={onPreviewClick}
				gicEntryPointProps={gicEntryPointProps}
				onBulkAccept={onBulkAccept}
				isStagingArea={isStagingArea}
				entryPoint={JIRA_PREVIEW_CARD_ENTRY_POINT}
				showSuccessFlagOnCreate={showSuccessFlagOnCreate}
				sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
			/>
		),
		[
			product,
			queryRef,
			cloudId,
			isCreated,
			isEnabled,
			isSelected,
			isProjectChangePending,
			onCreateSuccess,
			onCreateError,
			onDismissAll,
			onPreviewClick,
			projectIdOrKey,
			showSingleWorkItemCard,
			gicEntryPointProps,
			onBulkAccept,
			isStagingArea,
			messageId,
			showSuccessFlagOnCreate,
			sendAifcAnalyticsEvent,
		],
	);

	const memoizedFooterActions = useMemo(
		() => (
			<PreviewFooterActions
				product={product}
				query={queryRef}
				project={queryRef.jira_projectByIdOrKey!}
				areButtonsDisabled={isSelected || !isEnabled || isCreated || isProjectChangePending}
				cloudId={cloudId}
				messageId={messageId}
				projectIdOrKey={projectIdOrKey}
				isSingleWorkItem={showSingleWorkItemCard}
				onCreateSuccess={onCreateSuccess}
				onCreateError={onCreateError}
				onDismiss={onDismissAll}
				onPreviewClick={onPreviewClick}
				gicEntryPointProps={gicEntryPointProps}
				onBulkAccept={onBulkAccept}
				isStagingArea={isStagingArea}
				entryPoint={JIRA_PREVIEW_CARD_ENTRY_POINT}
				showSuccessFlagOnCreate={showSuccessFlagOnCreate}
				selectedItems={selectedItems}
				sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
			/>
		),
		[
			product,
			queryRef,
			cloudId,
			isCreated,
			isEnabled,
			isSelected,
			isProjectChangePending,
			onCreateSuccess,
			onCreateError,
			onDismissAll,
			onPreviewClick,
			projectIdOrKey,
			showSingleWorkItemCard,
			gicEntryPointProps,
			onBulkAccept,
			isStagingArea,
			messageId,
			showSuccessFlagOnCreate,
			selectedItems,
			sendAifcAnalyticsEvent,
		],
	);

	const footerActions =
		FeatureGates.getExperimentValue<string>(
			'jira-aifc-workflow-resumption-ui-updates',
			'cohort',
			'control',
			{ fireExperimentExposure: false },
		) === 'checkboxes' ? (
			memoizedFooterActions
		) : (
			<FooterActions />
		);

	// Opens the work item view in the global preview panel
	const openWorkItemView = useCallback(
		(createdIssueKey: string | undefined) => {
			if (!createdIssueKey || isStagingArea) {
				return;
			}
			sendAifcAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'createdWorkItem',
				actionSubjectId: 'createWorkItemsPreviewPanelOpened',
				attributes: {
					itemCount: initialActions.length,
					isSingleWorkItem: initialActions.length === 1,
					isStagingArea,
				},
			});
			openPreviewPanel({
				ari: createWorkItemAri(cloudId, createdIssueKey),
				name: 'Jira work item',
				url: `/browse/${createdIssueKey}`,
			});
		},
		[cloudId, openPreviewPanel, sendAifcAnalyticsEvent, initialActions.length, isStagingArea],
	);

	const getSystemMessage = useCallback(() => {
		if (
			FeatureGates.getExperimentValue<string>(
				'jira-aifc-workflow-resumption-ui-updates',
				'cohort',
				'control',
				{ fireExperimentExposure: false },
			) === 'checkboxes'
		) {
			const displayCount = draftWorkItems.reduce(
				(count, item) =>
					count +
					(item.createdIssueKey ? 1 : 0) +
					(item.childWorkItems?.filter((child) => Boolean(child.createdIssueKey)).length ?? 0),
				0,
			);

			return displayCount === 1 && primaryDraftWorkItem
				? formatMessage(messages.createdSuggestedWorkItem, {
						issueKey: primaryDraftWorkItem.createdIssueKey ?? '',
						summary: primaryDraftWorkItem.summary ?? '',
					})
				: formatMessage(messages.createdMultiWorkItem, {
						count: displayCount,
					});
		}

		return activeOrFinishedDraftWorkItemsCount === 1 && primaryDraftWorkItem
			? formatMessage(messages.createdSuggestedWorkItem, {
					issueKey: primaryDraftWorkItem.createdIssueKey ?? '',
					summary: primaryDraftWorkItem.summary ?? '',
				})
			: formatMessage(messages.createdMultiWorkItem, {
					count: activeOrFinishedDraftWorkItemsCount,
				});
	}, [activeOrFinishedDraftWorkItemsCount, formatMessage, primaryDraftWorkItem, draftWorkItems]);

	const sendChatMessageForProjectChange = useCallback(
		(projectKey: string) => {
			if (
				!FeatureGates.getExperimentValue(
					'jira-aifc-project-selector-in-chat-exp',
					'isEnabled',
					false,
					{ fireExperimentExposure: false },
				)
			) {
				return;
			}
			publish({
				type: 'insert-prompt',
				source: 'ai-mate',
				// The chat is already open so suppress the chat-opener side effect. Without this,
				// ChatOpenerSubscriber promotes the chat to MINI_MODAL whenever an 'ai-mate'-sourced event is published.
				openChat: false,
				data: {
					prompt: `/create-work-items ${formatMessage(messages.projectChangePrompt, { projectKey: projectKey })}`,
					placeholderType: 'skill',
				},
			});
			sendAifcAnalyticsEvent({
				action: 'selected',
				actionSubject: 'option',
				actionSubjectId: 'jiraAifcPreviewCardProjectSelector',
			});
			// Disable the footer actions while the new chat turn for the project change is created.
			setIsProjectChangePending(true);
		},
		[publish, formatMessage, sendAifcAnalyticsEvent],
	);

	// Show loading state in preview card while the store is being initialised or the BE is still fetching any saved state
	if (!isCreated && (isFetchingDraftWorkItems || !isStoreReady)) {
		return (
			<FallbackPreviewCard
				isSelected={isSelected}
				showSingleWorkItemCard={showSingleWorkItemCard}
			/>
		);
	}

	if (isDismissed) {
		return (
			<SystemMessage
				message={formatMessage(messages.dismissedSuggestedWorkItem)}
				isSuccessMessage={false}
			/>
		);
	}

	return (
		<Stack space="space.100">
			{/* Action Plan reliability signal: a valid create proposal has been presented to the
			    user (RovoSkillsPlanWorkItemCreate). Fired for both the in-chat and AIFC canvas
			    surfaces; the outer component's ExperienceStart tags the surface via isStagingArea. */}
			{fg('rovo_skills_work_item_reliability_ufo') && (
				<ExperienceSuccess name={ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_CREATE} />
			)}
			{showSingleWorkItemCard ? (
				<JiraSingleWorkItemPreviewCard
					cloudId={cloudId}
					queryRef={queryRef}
					draftWorkItems={draftWorkItems}
					isSelected={isSelected}
					isEnabled={isEnabled}
					isCreated={isCreated}
					previewFooterActions={footerActions}
					onFieldChange={onFieldChange}
					openWorkItemView={openWorkItemView}
					isStagingArea={isStagingArea}
					messageId={messageId}
					onProjectChange={sendChatMessageForProjectChange}
					sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
				/>
			) : (
				<JiraMultiWorkItemPreviewCard
					cloudId={cloudId}
					project={queryRef.jira_projectByIdOrKey!}
					projectKey={projectIdOrKey}
					draftWorkItems={draftWorkItems}
					isCreated={isCreated}
					isSelected={isSelected}
					isEnabled={isEnabled}
					previewFooterActions={footerActions}
					onDeclineWorkItem={onDeclineWorkItem}
					hideInlineButtons={
						isSelected || !isEnabled || isCreated || isProjectChangePending || hasAnyCreatedItems
					}
					openWorkItemView={openWorkItemView}
					onInlineAccept={onInlineAccept}
					gicEntryPointProps={gicEntryPointProps}
					isStagingArea={isStagingArea}
					onProjectChange={sendChatMessageForProjectChange}
					selectedItems={selectedItems}
					onToggleSelect={onToggleSelect}
					hasAnyCreatedItems={hasAnyCreatedItems}
					isCreating={
						FeatureGates.getExperimentValue<string>(
							'jira-aifc-workflow-resumption-ui-updates',
							'cohort',
							'control',
							{ fireExperimentExposure: false },
						) === 'checkboxes' && isCreating
					}
					sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
				/>
			)}
			{(FeatureGates.getExperimentValue<string>(
				'jira-aifc-workflow-resumption-ui-updates',
				'cohort',
				'control',
				{ fireExperimentExposure: false },
			) === 'checkboxes'
				? hasAnyCreatedItems
				: isCreated) && <SystemMessage message={getSystemMessage()} />}
			{errorMessage && <ErrorMessage errorMessage={errorMessage} />}
		</Stack>
	);
};

/**
 * Renders either the EAP version of the card or the new version of the card
 */
export const JiraCreateWithRovoPreviewCard = ({
	product,
	actions,
	messageId,
	invokedActions,
	cloudId,
	isSelected,
	isEnabled,
	isLastMessage,
	onPreviewClick,
	resolveActions,
	isStagingArea,
	conversationId,
	showSuccessFlagOnCreate,
}: {
	product?: string;
	actions: MessageAction[];
	messageId: string;
	invokedActions?: { [invocationId: string]: MessageActionInvocationStatus };
	cloudId: string;
	isSelected: boolean;
	isEnabled?: boolean;
	isLastMessage?: boolean;
	onPreviewClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	resolveActions: ResolveActionsFnc;
	isStagingArea: boolean;
	conversationId: string;
	showSuccessFlagOnCreate?: boolean;
}): React.JSX.Element | null => {
	const hasFetchedDraftActions = useRef(false);
	const { sendAifcAnalyticsEvent } = useAifcAnalytics(messageId);

	// Try fetch the draft work items from the backend as soon as this outer component mounts so it can be ready
	// before JiraCreateWithRovoPreviewCardNew's query is complete.
	useEffect(() => {
		if (
			!cloudId ||
			!conversationId ||
			!messageId ||
			!isLastMessage ||
			hasFetchedDraftActions.current
		) {
			return;
		}
		hasFetchedDraftActions.current = true;
		getOrFetchDraftWorkItemsWithEdits(cloudId, conversationId, messageId, sendAifcAnalyticsEvent);
	}, [cloudId, conversationId, messageId, isLastMessage, sendAifcAnalyticsEvent]);

	if (actions.length === 0 || !actions[0]) {
		return null;
	}

	const showSingleWorkItemCard = Boolean(actions.length === 1 && actions[0]);
	return (
		<ModalSyncBridgeAPI
			actions={actions}
			invokedActions={invokedActions ?? {}}
			resolveActions={resolveActions}
			isLastMessage={isLastMessage ?? false}
		>
			{({ actions: mappedActions }: { actions: MessageAction[] }) => (
				<>
					{/* Action Plan reliability signal (RovoSkillsPlanWorkItemCreate): start the
					    experience when the create proposal card mounts, before the inner card's data
					    query can suspend or throw, so it is running when the proposal is presented
					    (success) or fails to render (failure, fired from the error boundary). Placed
					    here — the shared card used by both the preview-card and migrated
					    action-renderer paths — so the signal is independent of that migration. The
					    isStagingArea attribute records whether it was the in-chat or AIFC canvas
					    surface, and totalCount the number of work items proposed; both propagate to
					    the emitted success/fail events. */}
					{fg('rovo_skills_work_item_reliability_ufo') && (
						<ExperienceStart
							name={ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_CREATE}
							attributes={{ isStagingArea, totalCount: actions.length }}
						/>
					)}
					<Suspense
						fallback={
							<FallbackPreviewCard
								isSelected={isSelected}
								showSingleWorkItemCard={showSingleWorkItemCard}
							/>
						}
					>
						<JiraCreateWithRovoPreviewCardNew
							product={product}
							actions={mappedActions}
							messageId={messageId}
							cloudId={cloudId}
							conversationId={conversationId}
							isSelected={isSelected}
							isEnabled={isEnabled}
							onPreviewClick={onPreviewClick}
							resolveActions={resolveActions}
							isStagingArea={isStagingArea}
							showSuccessFlagOnCreate={showSuccessFlagOnCreate}
						/>
					</Suspense>
				</>
			)}
		</ModalSyncBridgeAPI>
	);
};
